import { useState, useCallback } from 'react';

const RETRY_LIMIT = 10;

/**
 * Self-contained hook for fetching test retry/reproduce history.
 * No Redux dependency — manages state internally.
 *
 * @param {Object} options
 * @param {string} options.apiBaseUrl - Base URL for the API
 * @param {string} options.username   - Username for basic auth
 * @param {string} options.apiToken   - API token for basic auth
 */
export function useTestRetryHistory({ apiBaseUrl, username, apiToken }) {
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(
    async (parentTestId, debugType) => {
      if (!apiBaseUrl || !username || !apiToken || !parentTestId) return;

      setIsLoading(true);
      try {
        const endpoint = `${apiBaseUrl}/api/v2/test?timezone=null&offset=0&limit=${RETRY_LIMIT}&includeCreated=0&caps=1&sortBy=newest&parentTestId=${parentTestId}&debugType=${debugType}`;
        const res = await fetch(endpoint, {
          method: 'GET',
          headers: {
            Authorization: `Basic ${btoa(`${username}:${apiToken}`)}`,
            Accept: 'application/json',
          },
        });

        if (!res.ok) throw new Error(`API error: ${res.status}`);

        const result = await res.json();
        setData(result || {});
        setError(null);
      } catch (err) {
        setError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [apiBaseUrl, username, apiToken],
  );

  const reset = useCallback(() => {
    setData({});
    setError(null);
    setIsLoading(false);
  }, []);

  return { data, isLoading, error, fetchHistory, reset };
}
